import React, { useEffect, useState, useRef } from 'react'
import { Row, Col, Button, Form, Input, Select, Spin, Modal } from 'antd'
import { toast } from 'react-toastify'
import axiosInstance from '../../Utils/axiosInstance'
import {
  ContactsOutlined,
  PrinterOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import { DatePicker } from 'antd'
import Logo from '../../Assets/logo.png'
import dayjs from 'dayjs'
import {
  AvailableSlotData,
  GetFeeAmountData,
  SaveManualUserData,
} from "../../API's/AdminApi"
import { BaseURLPdf } from "../../API's/UserApi"

const { Option } = Select
const { TextArea } = Input

/* ─── helpers ─────────────────────────────────────────────────────── */
const GENDER_LABEL = {
  1: 'Male',
  2: 'Female',
  3: 'Male & Female',
}

const genderMeta = (g) => ({
  label: GENDER_LABEL[g] ?? 'N/A',
  accent: g === 1 ? '#378ADD' : g === 2 ? '#D4537E' : '#1D9E75',
  badgeBg: g === 1 ? '#E6F1FB' : g === 2 ? '#FBEAF0' : '#E1F5EE',
  badgeColor: g === 1 ? '#0C447C' : g === 2 ? '#72243E' : '#085041',
  icon: g === 1 ? '♂' : g === 2 ? '♀' : '⚥',
})

const intakeCount = (slot) => parseInt(slot.intakeAvailable ?? '0', 10)

/* ─── styles ───────────────────────────────────────────────────────── */
const S = {
  page: {
    minHeight: '100vh',
    background: '#e8f3ff',
    padding: '24px 20px',
    fontFamily: "'DM Sans', sans-serif",
  },

  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },

  pageTitle: {
    fontSize: 22,
    fontWeight: 600,
    color: '#1a1a2e',
    margin: 0,
  },

  pageSub: {
    fontSize: 13,
    color: '#6b7280',
    marginBottom: 24,
    marginTop: 4,
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 16,
  },

  card: (selected, unavailable) => ({
    background: unavailable ? '#fafafa' : '#ffffff',
    border: unavailable
      ? '1px dashed #d1d5db'
      : selected
      ? '2px solid #185FA5'
      : '1px solid #e5e7eb',
    borderRadius: 14,
    padding: '1.25rem',
    cursor: unavailable ? 'not-allowed' : 'pointer',
    position: 'relative',
    overflow: 'hidden',
    opacity: unavailable ? 0.65 : 1,
    transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
    boxShadow: selected
      ? '0 0 0 4px rgba(24, 95, 165, 0.12)'
      : '0 1px 3px rgba(0,0,0,0.06)',
  }),

  accentBar: (color, unavailable) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: unavailable ? '#d1d5db' : color,
    borderRadius: '14px 14px 0 0',
  }),

  selectedChip: {
    position: 'absolute',
    top: 12,
    right: 12,
    background: '#185FA5',
    color: '#fff',
    fontSize: 10,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 20,
    letterSpacing: 0.5,
  },

  badge: (bg, color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 11,
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: 20,
    background: bg,
    color: color,
    marginBottom: 14,
    marginTop: 8,
    letterSpacing: 0.3,
  }),

  timeFrom: {
    fontSize: 24,
    fontWeight: 700,
    color: '#111827',
    lineHeight: 1.1,
  },

  timeTo: {
    fontSize: 13,
    color: '#6b7280',
    marginTop: 4,
  },

  divider: {
    border: 'none',
    borderTop: '1px solid #f0f0f0',
    margin: '12px 0',
  },

  metaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 12,
    color: '#6b7280',
  },

  intakePill: (count) => ({
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 9px',
    borderRadius: 20,
    background: count === 0 ? '#fef2f2' : count <= 5 ? '#fff7ed' : '#f0fdf4',
    color: count === 0 ? '#991b1b' : count <= 5 ? '#9a3412' : '#166534',
  }),

  unavailableBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 8,
    padding: '7px 10px',
    fontSize: 12,
    color: '#991b1b',
    fontWeight: 500,
    marginTop: 10,
  },

  bookBtn: (selected) => ({
    marginTop: 14,
    width: '100%',
    background: selected ? '#185FA5' : 'transparent',
    border: selected ? '1px solid #185FA5' : '1px solid #d1d5db',
    borderRadius: 8,
    padding: '8px 0',
    fontSize: 13,
    color: selected ? '#ffffff' : '#374151',
    cursor: 'pointer',
    fontWeight: 500,
  }),

  formWrap: {
    background: '#ffffff',
    borderRadius: 16,
    border: '1px solid #e5e7eb',
    padding: '28px 32px',
    maxWidth: 900,
    margin: '0 auto',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },

  formTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1a1a2e',
    marginBottom: 20,
  },

  slotInfo: {
    background: '#f0f6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 10,
    padding: '12px 16px',
    marginBottom: 20,
    fontSize: 13,
    color: '#1e40af',
  },

  ageFeeBox: (fee) => ({
    marginBottom: 16,
    padding: '10px 14px',
    background: fee === 100 ? '#eff6ff' : '#fffbeb',
    border: `1px solid ${fee === 100 ? '#bfdbfe' : '#fde68a'}`,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    fontSize: 14,
  }),

  feeAmount: (fee) => ({
    color: fee === 100 ? '#1d4ed8' : '#b45309',
    fontWeight: 700,
    fontSize: 17,
  }),
}

/* ══════════════════════════════════════════════════════════════════ */

export default function TicketBooking() {
  const [slots, setSlots] = useState([])
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)

  const [members, setMembers] = useState([
    {
      name: '',
      dob: null,
      gender: null,
      mobile: '',
      address: '',
      age: null,
      fee: null,
    },
  ])

  const [showDeclaration, setShowDeclaration] = useState(false)
  const [formValues, setFormValues] = useState(null)

  const declarationRef = useRef(null)

  const [form] = Form.useForm()

  /* ───────────────── FETCH SLOTS ───────────────── */

  const getSlots = async () => {
    try {
      setLoading(true)

      const res = await axiosInstance.get(AvailableSlotData)

      setSlots(res.data || [])
    } catch {
      toast.error('Failed to load slots')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getSlots()
  }, [])

  /* ───────────────── MEMBERS ───────────────── */

  const addMember = () => {
    setMembers([
      ...members,
      {
        name: '',
        dob: null,
        gender: null,
        mobile: '',
        address: '',
        age: null,
        fee: null,
      },
    ])
  }

  const removeMember = (index) => {
    const updated = [...members]
    updated.splice(index, 1)
    setMembers(updated)
  }

  const handleMemberChange = (index, field, value) => {
    const updated = [...members]
    updated[index][field] = value
    setMembers(updated)
  }

  /* ───────────────── DOB & FEE ───────────────── */

  const handleDobChange = async (date, index) => {
    if (!date) return

    const age = dayjs().diff(date, 'year')

    const updated = [...members]

    updated[index].dob = date
    updated[index].age = age

    try {
      const res = await axiosInstance.get(
        `${GetFeeAmountData}?birthDate=${date.format('YYYY-MM-DD')}`,
      )

      updated[index].fee = res.data?.amount ?? res.data

      setMembers(updated)
    } catch {
      toast.error('Failed to fetch fee')
    }
  }

  /* ───────────────── NEXT ───────────────── */

  const onFinish = () => {
    for (let index = 0; index < members.length; index++) {
      const member = members[index]

      if (
        !member.name ||
        !member.dob ||
        !member.gender ||
        (index === 0 && !member.mobile) ||
        (index === 0 && !member.address)
      ) {
        toast.error('Please fill all required details')
        return
      }
    }

    setFormValues({
      members,
      slotTimeFrom: selectedSlot?.timeFrom,
      slotTimeTo: selectedSlot?.timeTo,
    })

    setShowDeclaration(true)
  }

  /* ───────────────── SAVE ───────────────── */

  const handleDeclarationConfirm = async () => {
    try {
      setSubmitLoading(true)

      const payload = {
        slotId: selectedSlot?.id,

        members: formValues.members.map((member) => ({
          name: member.name,

          birthDate: member.dob ? dayjs(member.dob).format('YYYY-MM-DD') : null,

          gender: member.gender,

          mobileNo: member.mobile,

          address: member.address,

          amount: member.fee,
        })),
      }

      const res = await axiosInstance.post(SaveManualUserData, payload)

      const data = res.data

      if (
        data?.message === 'BOOKING_CONFIRMED' ||
        res.status === 200 ||
        res.status === 201
      ) {
        toast.success('Ticket booked successfully!')

        if (data?.pdfName) {
          const pdfUrl = `${BaseURLPdf}${data.pdfName}`

          window.open(pdfUrl, '_blank')
        }

        form.resetFields()

        setShowForm(false)

        setSelectedSlot(null)

        setShowDeclaration(false)

        setFormValues(null)

        setMembers([
          {
            name: '',
            dob: null,
            gender: null,
            mobile: '',
            address: '',
            age: null,
            fee: null,
          },
        ])

        getSlots()
      } else {
        toast.warning(data?.message || 'Unexpected response. Please try again.')
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        'Submission failed.'

      toast.error(typeof msg === 'string' ? msg : 'Submission failed.')
    } finally {
      setSubmitLoading(false)
    }
  }

  /* ───────────────── PRINT ───────────────── */

  const handlePrint = () => {
  const printContents = declarationRef.current.innerHTML

  const iframe = document.createElement('iframe')

  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'

  document.body.appendChild(iframe)

  const doc = iframe.contentWindow.document

  doc.open()

  doc.write(`
    <html>
      <head>
        <title>Declaration</title>

        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 30px;
            font-size: 13px;
            color: #000;
          }

          .declaration-box {
            max-width: 900px;
            margin: auto;
          }

          p {
            margin: 6px 0;
          }

          h4 {
            margin-bottom: 10px;
          }

          ol {
            padding-left: 18px;
          }

          li {
            margin-bottom: 6px;
            line-height: 1.6;
          }
        </style>
      </head>

      <body>
        <div class="declaration-box">
          ${printContents}
        </div>
      </body>
    </html>
  `)

  doc.close()

  iframe.onload = () => {
    iframe.contentWindow.focus()
    iframe.contentWindow.print()

    setTimeout(() => {
      document.body.removeChild(iframe)
    }, 1000)
  }
}

  /* ───────────────── BACK ───────────────── */

  const handleBack = () => {
    setShowForm(false)

    setSelectedSlot(null)

    form.resetFields()

    setMembers([
      {
        name: '',
        dob: null,
        gender: null,
        mobile: '',
        address: '',
        age: null,
        fee: null,
      },
    ])
  }

  /* ═════════════════ RENDER ═════════════════ */

  return (
    <div style={S.page}>
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      {/* HEADER */}

      <div style={S.pageHeader}>
        <ContactsOutlined
          style={{
            fontSize: 22,
            color: '#185FA5',
          }}
        />

        <h2 style={S.pageTitle}>Ticket Booking</h2>
      </div>

      {/* SLOT LIST */}

      {!showForm && (
        <Spin spinning={loading}>
          <p style={S.pageSub}>
            Select an available time slot to begin your booking.
          </p>

          <div style={S.grid}>
            {slots.map((slot) => {
              const meta = genderMeta(slot.gender)

              const isSelected = selectedSlot?.id === slot.id

              const available = intakeCount(slot)

              const unavailable = available === 0

              return (
                <div key={slot.id} style={S.card(isSelected, unavailable)}>
                  <div style={S.accentBar(meta.accent, unavailable)} />

                  {isSelected && !unavailable && (
                    <span style={S.selectedChip}>✓ Selected</span>
                  )}

                  <div style={S.badge(meta.badgeBg, meta.badgeColor)}>
                    <span>{meta.icon}</span>

                    {meta.label}
                  </div>

                  <div style={S.timeFrom}>{slot.timeFrom}</div>

                  <div style={S.timeTo}>↓ to {slot.timeTo}</div>

                  <hr style={S.divider} />

                  <div style={S.metaRow}>
                    <span>⏱ 60 min</span>

                    <span style={S.intakePill(available)}>
                      {available} seats
                    </span>
                  </div>

                  {!unavailable ? (
                    <button
                      style={S.bookBtn(isSelected)}
                      onClick={() => {
                        setSelectedSlot(slot)
                        setShowForm(true)
                      }}
                    >
                      Book this slot
                    </button>
                  ) : (
                    <div style={S.unavailableBanner}>🚫 Slot Full</div>
                  )}
                </div>
              )
            })}
          </div>
        </Spin>
      )}

      {/* FORM */}

      {showForm && (
        <div style={S.formWrap}>
          <div style={S.formTitle}>Fill Member Details</div>

          {selectedSlot && (
            <div style={S.slotInfo}>
              <strong>Selected Slot:</strong> {selectedSlot.timeFrom} -{' '}
              {selectedSlot.timeTo}
            </div>
          )}

          <Form form={form} onFinish={onFinish}>
            {members.map((member, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 20,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 16,
                  }}
                >
                  <h3>Member {index + 1}</h3>

                  {members.length > 1 && (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeMember(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>

                <Row gutter={16}>
                  {/* NAME */}

                  <Col xs={24} md={12}>
                    <Form.Item label="Full Name">
                      <Input
                        value={member.name}
                        onChange={(e) =>
                          handleMemberChange(index, 'name', e.target.value)
                        }
                        placeholder="Enter full name"
                        size="large"
                      />
                    </Form.Item>
                  </Col>

                  {/* DOB */}

                  <Col xs={24} md={12}>
                    <Form.Item label="Date of Birth">
                      <DatePicker
                        style={{ width: '100%' }}
                        size="large"
                        format="DD-MM-YYYY"
                        value={member.dob}
                        disabledDate={(c) => c && c > dayjs().endOf('day')}
                        onChange={(date) => handleDobChange(date, index)}
                      />
                    </Form.Item>
                  </Col>

                  {/* AGE & FEE */}

                  {member.age !== null && (
                    <Col xs={24}>
                      <div style={S.ageFeeBox(member.fee)}>
                        <span>
                          <strong>Age:</strong> {member.age}
                        </span>

                        <span>
                          <strong>Fee:</strong>{' '}
                          <span style={S.feeAmount(member.fee)}>
                            ₹{member.fee}
                          </span>
                        </span>
                      </div>
                    </Col>
                  )}

                  {/* GENDER */}

                  <Col xs={24} md={12}>
                    <Form.Item label="Gender">
                      <Select
                        value={member.gender}
                        placeholder="Select Gender"
                        size="large"
                        onChange={(value) =>
                          handleMemberChange(index, 'gender', value)
                        }
                      >
                        <Option value={1}>Male</Option>

                        <Option value={2}>Female</Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  {/* ADDRESS & MOBILE - ONLY FIRST MEMBER */}

                  {index === 0 && (
                    <>
                      <Col xs={24} md={12}>
                        <Form.Item label="Mobile Number">
                          <Input
                            value={member.mobile}
                            onChange={(e) =>
                              handleMemberChange(
                                index,
                                'mobile',
                                e.target.value,
                              )
                            }
                            placeholder="Enter mobile"
                            maxLength={10}
                            size="large"
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24}>
                        <Form.Item label="Address">
                          <TextArea
                            value={member.address}
                            onChange={(e) =>
                              handleMemberChange(
                                index,
                                'address',
                                e.target.value,
                              )
                            }
                            rows={3}
                            placeholder="Enter full address"
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>
              </div>
            ))}

            {/* ADD MEMBER */}

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addMember}
              style={{
                width: '100%',
                marginBottom: 20,
              }}
            >
              Add Member
            </Button>

            {/* ACTIONS */}

            <Form.Item>
              <Button style={{ marginRight: 10 }} onClick={handleBack}>
                Back
              </Button>

              <Button type="primary" htmlType="submit">
                Next
              </Button>
            </Form.Item>
          </Form>
        </div>
      )}

      {/* DECLARATION */}

      <Modal
        open={showDeclaration}
        onCancel={() => setShowDeclaration(false)}
        width={780}
        footer={null}
        title="User Declaration & Indemnity"
        centered
      >
        <div ref={declarationRef}>
          <div>
            {/* MEMBERS */}

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                borderBottom: '2px solid #333',
                paddingBottom: 12,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <img
                  src={Logo}
                  alt="logo"
                  style={{ width: 56, height: 56, objectFit: 'contain' }}
                />
              </div>
              <div>
                <div
                  style={{ fontWeight: 'bold', fontSize: 15, color: '#1a1a1a' }}
                >
                  THE ROTARY SILVER JUBILEE TRUST, SWIMMING POOL, SANGLI.
                </div>
                <div
                  style={{
                    fontWeight: 'bold',
                    fontSize: 13,
                    color: '#333',
                    marginTop: 2,
                  }}
                >
                  वापरकर्ता घोषणा व जबाबदारीमुक्ती पत्र (User Declaration &
                  Indemnity)
                </div>
              </div>
            </div>

            <p style={{ fontSize: 12, marginBottom: 10 }}>
              मी, खाली सही करणारा/करणारी, ट्रस्टद्वारे चालविल्या जाणाऱ्या जलतरण
              तलावाचा वापर करण्यासाठी परवानगी मागत आहे.
            </p>

            <ol style={{ paddingLeft: 18, margin: 0, fontSize: 12 }}>
              {[
                'मी जाहीर करतो/करते की मी शारीरिकदृष्ट्या सक्षम असून पोहणे / जलतरणाशी संबंधित क्रियाकलाप करण्यास योग्य आहे. माझ्या सुरक्षिततेस धोका निर्माण होईल अशी कोणतीही वैद्यकीय समस्या मला नाही.',
                'जलतरण तलाव परिसरात लावलेले सर्व सुरक्षा नियम, सूचना तसेच लाइफगार्ड/प्रशिक्षक यांच्या सूचना मी काटेकोरपणे पाळण्यास सहमत आहे.',
                'मला माहीत आहे की पोहणे ही शारीरिक क्रिया असून त्यात अपघाती दुखापत, घसरणे किंवा बुडण्याचा धोका अंतर्भूत असतो.',
                'मी मान्य करतो/करते की जलतरण तलावाचा वापर मी पूर्णपणे माझ्या स्वतःच्या जोखमीवर व जबाबदारीवर करीत आहे.',
                'खालील कारणांमुळे घडणाऱ्या कोणत्याही अपघात, दुखापत, आजारपण किंवा नुकसानाबद्दल ट्रस्ट, त्यांचे अध्यक्ष, विश्वस्त, कर्मचारी, प्रशिक्षक, लाइफगार्ड किंवा इतर कर्मचारी यांना जबाबदार धरले जाणार नाही, यास मी सहमत आहे : * माझी स्वतःची निष्काळजीपणा, * जलतरण तलावाच्या नियमांचे पालन न करणे, * कोणतीही अनपेक्षित किंवा टाळता न येणारी घटना',
                'माझ्या सुरक्षिततेवर परिणाम करू शकणारी कोणतीही वैद्यकीय समस्या असल्यास ती मी तत्काळ जलतरण तलाव प्रशासनास कळवण्याची जबाबदारी स्वीकारतो/स्वीकारते.',
                'आपत्कालीन परिस्थितीत प्रथमोपचार देणे तसेच आवश्यक असल्यास वैद्यकीय उपचारांची व्यवस्था करण्यास मी जलतरण तलाव प्रशासनास परवानगी देतो/देते.',
                'मी जाहीर करतो/करते की जलतरण तलावाचे सर्व नियम मी वाचले आहेत, समजले आहेत आणि त्यांचे पालन करण्यास सहमत आहे.',
              ].map((text, i) => (
                <li key={i} style={{ marginBottom: 5, lineHeight: 1.6 }}>
                  {text}
                </li>
              ))}
            </ol>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 16,
            }}
          >
            {' '}
            {formValues?.members?.map((member, index) => (
              <div
                key={index}
                style={{
                  border: '1px dashed #999',
                  borderRadius: 8,
                  padding: 12,
                }}
              >
                {' '}
                <h6 style={{ marginBottom: 16 }}>Member {index + 1}</h6>{' '}
                <p>
                  {' '}
                  <strong>Name:</strong> {member.name}{' '}
                </p>{' '}
                <p>
                  {' '}
                  <strong>Mobile:</strong> {member.mobile}{' '}
                </p>{' '}
                <p>
                  {' '}
                  <strong>Gender:</strong> {GENDER_LABEL[member.gender]}{' '}
                </p>{' '}
              </div>
            ))}{' '}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            marginTop: 20,
          }}
        >
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            Print
          </Button>

          <Button onClick={() => setShowDeclaration(false)}>Cancel</Button>

          <Button
            type="primary"
            loading={submitLoading}
            onClick={handleDeclarationConfirm}
          >
            Confirm & Submit
          </Button>
        </div>
      </Modal>
    </div>
  )
}
